import { Col, Row } from "antd";
import cuid from "cuid";
import dayjs from "dayjs";
import { get, set } from "lodash";
import { useTranslation } from "react-i18next";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  DATE_FORMAT,
  MILTIS_CSP_PROFESSIONS,
  RELATIONSHIP,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";
import {
  adultBirthDateDisabledDate,
  adultBirthDatePickerValue,
} from "utils/helpers/date";
import {
  buildObsequesCoupleDisabled,
  isObsequesChildrenBlockDisabled,
  isSpouseIncluded,
} from "../../../obseques";

const createEmptyChild = () => ({
  id: cuid(),
  relationship: RELATIONSHIP.CHILD,
  identity: { id: cuid(), user: { id: cuid() } },
});

const resizeChildren = (initialValues, count) => {
  const current = get(initialValues, "children", []) || [];
  if (current.length === count) return;
  if (current.length < count) {
    const next = [...current];
    while (next.length < count) {
      next.push(createEmptyChild());
    }
    set(initialValues, "children", next);
    return;
  }
  set(initialValues, "children", current.slice(0, count));
};

const refreshCoupleDisabled = (initialValues, setDisabled) => {
  if (typeof setDisabled !== "function") return;
  const coupleDisabled = buildObsequesCoupleDisabled(initialValues);
  setDisabled((prev) => {
    const next = { ...prev };
    Object.entries(coupleDisabled).forEach(([path, value]) => {
      set(next, path, value);
    });
    next[SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CHILDREN] =
      isObsequesChildrenBlockDisabled(initialValues);
    return next;
  });
};

const Partner = ({
  setPercent,
  isFieldRequired,
  bindField,
  initialValues,
  setDisabled,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const coupleIncluded = isSpouseIncluded(initialValues);
  const birthDate = get(
    initialValues,
    "spouse.identity.user.birthDate",
    undefined,
  );
  const spouseIncludedPath = "project.fields.spouseIncluded";
  const spouseIncludedField = bindField(spouseIncludedPath, {
    label: SUBSCRIPTION.FORM.SPOUSE_INCLUDED.LABEL,
    required: isFieldRequired(spouseIncludedPath),
    onChange: (value) => {
      const included = value === true || value === "true";
      set(initialValues, spouseIncludedPath, included ? "true" : "false");
      if (included && !get(initialValues, "spouse.identity.user")) {
        set(initialValues, "spouse", {
          identity: { user: {}, fields: {} },
        });
      }
      if (!included) {
        set(initialValues, "project.fields.childrenCount", 0);
        set(initialValues, "children", []);
      }
      refreshCoupleDisabled(initialValues, setDisabled);
      setPercent(initialValues);
    },
  });
  const memberNumberPath = "spouse.identity.fields.memberNumber";
  const memberNumberField = bindField(memberNumberPath, {
    label: SUBSCRIPTION.FORM.MEMBER_NUMBER.LABEL,
    required: false,
    onBlur: () => setPercent(initialValues),
  });
  const childrenCountPath = "project.fields.childrenCount";
  const childrenCountField = bindField(childrenCountPath, {
    label: SUBSCRIPTION.FORM.CHILDREN_COUNT.LABEL,
    required: isFieldRequired(childrenCountPath),
    onChange: (value) => {
      const count = Math.max(0, Number(value) || 0);
      set(initialValues, childrenCountPath, count);
      resizeChildren(initialValues, count);
      refreshCoupleDisabled(initialValues, setDisabled);
      setPercent(initialValues);
    },
    onBlur: () => setPercent(initialValues),
  });
  const spouseBirthDatePath = "spouse.identity.user.birthDate";
  const spouseBirthDateField = bindField(spouseBirthDatePath, {
    label: SUBSCRIPTION.FORM.BIRTH_DATE.LABEL,
    required: isFieldRequired(spouseBirthDatePath),
    onBlur: () => setPercent(initialValues),
  });

  const lastnamePath = "spouse.identity.user.lastname";
  const lastnameField = bindField(lastnamePath, {
    label: SUBSCRIPTION.FORM.LAST_NAME.LABEL,
    required: isFieldRequired(lastnamePath),
    onBlur: () => setPercent(initialValues),
  });

  const firstnamePath = "spouse.identity.user.firstname";
  const firstnameField = bindField(firstnamePath, {
    label: SUBSCRIPTION.FORM.FIRST_NAME.LABEL,
    required: isFieldRequired(firstnamePath),
    onBlur: () => setPercent(initialValues),
  });

  const socioProfessionalCategoryPath =
    "spouse.identity.socioProfessionalCategory";
  const socioProfessionalCategoryField = bindField(
    socioProfessionalCategoryPath,
    {
      label: SUBSCRIPTION.FORM.SOCIO_PROFESSIONAL_CATEGORY.LABEL,
      required: isFieldRequired(socioProfessionalCategoryPath),
      onChange: (value) => {
        const profession = MILTIS_CSP_PROFESSIONS.find(
          (item) => item.value === value,
        );
        set(
          initialValues,
          "spouse.identity.exerciseFrame",
          profession ? `${profession.label} (${profession.code})` : null,
        );
        setPercent(initialValues);
      },
      onBlur: () => setPercent(initialValues),
    },
  );

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PARTNER}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PARTNER.TITLE}
        subtitle={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.PARTNER.SUBTITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <FormField
              path={spouseIncludedField.path}
              label={spouseIncludedField.label}
              required={spouseIncludedField.required}
              disabled={spouseIncludedField.disabled}
            >
              {(field) => (
                <Input
                  type="radio"
                  variant="group"
                  values={[
                    {
                      value: "true",
                      label: SUBSCRIPTION.FORM.SPOUSE_INCLUDED.YES,
                    },
                    {
                      value: "false",
                      label: SUBSCRIPTION.FORM.SPOUSE_INCLUDED.NO,
                    },
                  ]}
                  label={spouseIncludedField.label}
                  value={coupleIncluded ? "true" : "false"}
                  {...field}
                  onChange={spouseIncludedField.onChange}
                  onBlur={spouseIncludedField.onBlur}
                />
              )}
            </FormField>
          </Col>
          {coupleIncluded && (
            <>
              <Col lg={24} xs={24}>
                <FormField
                  path={lastnameField.path}
                  label={lastnameField.label}
                  required={lastnameField.required}
                  disabled={lastnameField.disabled}
                >
                  {(field) => (
                    <Input
                      placeholder={SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER}
                      label={lastnameField.label}
                      type="custom"
                      normalizeText={true}
                      defaultValue={lastnameField.defaultValue}
                      {...field}
                      onChange={lastnameField.onChange}
                      onBlur={lastnameField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={firstnameField.path}
                  label={firstnameField.label}
                  required={firstnameField.required}
                  disabled={firstnameField.disabled}
                >
                  {(field) => (
                    <Input
                      label={firstnameField.label}
                      placeholder={SUBSCRIPTION.FORM.FIRST_NAME.PLACEHOLDER}
                      type="custom"
                      normalizeText={true}
                      defaultValue={firstnameField.defaultValue}
                      {...field}
                      onChange={firstnameField.onChange}
                      onBlur={firstnameField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={spouseBirthDateField.path}
                  label={spouseBirthDateField.label}
                  required={spouseBirthDateField.required}
                  disabled={spouseBirthDateField.disabled}
                >
                  {(field) => (
                    <Input
                      label={spouseBirthDateField.label}
                      placeholder={SUBSCRIPTION.FORM.BIRTH_DATE.PLACEHOLDER}
                      defaultValue={
                        (get(initialValues, spouseBirthDatePath) ||
                          birthDate) &&
                        dayjs(
                          get(initialValues, spouseBirthDatePath) || birthDate,
                        )
                      }
                      type="date"
                      variant="simple"
                      format={DATE_FORMAT}
                      disabledDate={adultBirthDateDisabledDate}
                      defaultPickerValue={adultBirthDatePickerValue}
                      {...field}
                      onChange={spouseBirthDateField.onChange}
                      onBlur={spouseBirthDateField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={socioProfessionalCategoryField.path}
                  label={socioProfessionalCategoryField.label}
                  required={socioProfessionalCategoryField.required}
                  disabled={socioProfessionalCategoryField.disabled}
                >
                  {(field) => (
                    <Select
                      type="simple"
                      label={socioProfessionalCategoryField.label}
                      placeholder={
                        SUBSCRIPTION.FORM.SOCIO_PROFESSIONAL_CATEGORY
                          .PLACEHOLDER
                      }
                      options={MILTIS_CSP_PROFESSIONS.map((item) => ({
                        value: item.value,
                        label: item.label,
                      }))}
                      defaultValue={socioProfessionalCategoryField.defaultValue}
                      {...field}
                      onChange={socioProfessionalCategoryField.onChange}
                      onBlur={socioProfessionalCategoryField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={memberNumberField.path}
                  label={memberNumberField.label}
                  required={memberNumberField.required}
                  disabled={memberNumberField.disabled}
                >
                  {(field) => (
                    <Input
                      label={memberNumberField.label}
                      placeholder={SUBSCRIPTION.FORM.MEMBER_NUMBER.PLACEHOLDER}
                      defaultValue={memberNumberField.defaultValue}
                      {...field}
                      onChange={memberNumberField.onChange}
                      onBlur={memberNumberField.onBlur}
                      optional={true}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={childrenCountField.path}
                  label={childrenCountField.label}
                  required={childrenCountField.required}
                  disabled={childrenCountField.disabled}
                >
                  {(field) => (
                    <Input
                      type="number"
                      label={childrenCountField.label}
                      placeholder={SUBSCRIPTION.FORM.CHILDREN_COUNT.PLACEHOLDER}
                      defaultValue={childrenCountField.defaultValue}
                      {...field}
                      onChange={childrenCountField.onChange}
                      onBlur={childrenCountField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
            </>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default Partner;
